
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { Filter, MousePointerClick, Search, CircleOff } from 'lucide-react';
import { PainelStatCard } from '@/components/painel/PainelStatCard';
import { painelTableShellClass } from '@/components/painel/painel-styles';
import { cn } from '@/lib/utils';
import { useAnalyticsSearch } from '@/hooks/use-analytics-search';
import { AnalyticsPageChrome, AnalyticsPanel, AnalyticsPeriodBadge } from '@/components/painel/analytics/AnalyticsPageChrome';
import { usePainelText } from '@/components/painel/painel-texts-context';

function formatInt(n: number) {
  return new Intl.NumberFormat('pt-BR').format(n);
}

export function PainelAnalyticsPesquisasPage() {
  const pageText = usePainelText('analytics_pesquisas');
  const { data } = useAnalyticsSearch();
  const topQueries = data.topQueries;
  const zeroResults = data.zeroResults;
  const clickthrough = data.clickthrough;
  const table = data.table.length > 0
    ? data.table
    : topQueries.map((q) => ({
        query: q.query,
        count: q.count,
        resultCount: 0,
        lastSearchedAt: null as string | null,
      }));

  return (
    <AnalyticsPageChrome
      title={pageText.title}
      description={pageText.description}
      actions={<AnalyticsPeriodBadge>Últimos 7 dias</AnalyticsPeriodBadge>}
    >
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <PainelStatCard
          title="Pesquisas totais"
          value={formatInt(data.totalSearches)}
          icon={Search}
          trend={data.searchesTrend ?? undefined}
          variant="sky"
        />
        <PainelStatCard
          title="CTR médio"
          value={data.avgCtrLabel}
          icon={MousePointerClick}
          description="Cliques nos resultados da busca"
          trend={data.ctrTrend ?? undefined}
          variant="cyan"
        />
        <PainelStatCard
          title="Sem cliques"
          value={data.noClickRateLabel}
          icon={Filter}
          description="Sessões que abandonaram resultados"
          trend={data.noClickTrend ?? undefined}
          variant="indigo"
        />
        <PainelStatCard
          title="Zero resultados"
          value={formatInt(data.zeroResultEvents || zeroResults.reduce((a, z) => a + z.hits, 0))}
          icon={CircleOff}
          description="Eventos sem retorno"
          variant="violet"
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <AnalyticsPanel
          title="Cliques por termo (CTR estimado)"
          subtitle="Top 5 consultas normalizadas"
        >
          <div className="h-[260px] w-full">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={clickthrough} layout="vertical" margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" horizontal={false} />
                <XAxis type="number" domain={[0, 100]} tickFormatter={(v) => `${v}%`} tick={{ fill: 'rgba(191,219,254,0.45)', fontSize: 11 }} />
                <YAxis
                  type="category"
                  dataKey="name"
                  width={88}
                  tick={{ fill: 'rgba(191,219,254,0.55)', fontSize: 11 }}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip
                  contentStyle={{
                    background: 'rgba(15,23,42,0.96)',
                    border: '1px solid rgba(56,189,248,0.28)',
                    borderRadius: 10,
                    fontSize: 12,
                  }}
                  formatter={(value: number, _n, p) => [`${value}% CTR`, (p?.payload as { full: string }).full]}
                />
                <Bar dataKey="ctr" fill="#22d3ee" radius={[0, 6, 6, 0]} barSize={14} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </AnalyticsPanel>

        <AnalyticsPanel
          title="Termos com pouco retorno"
          subtitle="Oportunidades de novos guias ou sinônimos"
        >
          <div className={cn(painelTableShellClass, 'divide-y divide-white/[0.06]')}>
            <div className="grid grid-cols-[1fr_auto] gap-3 bg-white/[0.03] px-4 py-2.5 font-poppins text-[10px] font-semibold uppercase tracking-wider text-sky-200/50">
              <span>Consulta</span>
              <span className="text-right">Tentativas</span>
            </div>
            {zeroResults.map((z) => (
              <div key={z.query} className="grid grid-cols-[1fr_auto] gap-3 px-4 py-3 font-inter text-sm">
                <span className="text-blue-100/90">{z.query}</span>
                <span className="text-right tabular-nums text-slate-400">{formatInt(z.hits)}</span>
              </div>
            ))}
            {zeroResults.length === 0 ? (
              <p className="px-4 py-3 font-inter text-xs text-slate-400/80">Nenhum termo sem resultados no período.</p>
            ) : null}
          </div>
        </AnalyticsPanel>
      </div>

      <AnalyticsPanel title="Todas as consultas em destaque" subtitle="Volume bruto de eventos">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[520px] border-separate border-spacing-0">
            <thead>
              <tr className="font-poppins text-[10px] font-semibold uppercase tracking-wider text-sky-200/50">
                <th className="border-b border-white/10 pb-3 text-left">Busca</th>
                <th className="border-b border-white/10 pb-3 text-right">Quantidade</th>
                <th className="border-b border-white/10 pb-3 text-right">Resultados</th>
                <th className="border-b border-white/10 pb-3 text-right">Última pesquisa</th>
              </tr>
            </thead>
            <tbody className="font-inter text-sm">
              {table.map((q) => (
                <tr key={q.query} className="text-blue-100/90">
                  <td className="border-b border-white/[0.05] py-3 pr-4">{q.query}</td>
                  <td className="border-b border-white/[0.05] py-3 text-right tabular-nums text-slate-400">
                    {formatInt(q.count)}
                  </td>
                  <td className="border-b border-white/[0.05] py-3 text-right tabular-nums text-slate-400">
                    {formatInt(q.resultCount)}
                  </td>
                  <td className="border-b border-white/[0.05] py-3 text-right text-slate-400">
                    {q.lastSearchedAt
                      ? new Date(q.lastSearchedAt).toLocaleString('pt-BR', {
                          day: '2-digit',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })
                      : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AnalyticsPanel>
    </AnalyticsPageChrome>
  );
}
